import { safeNumber } from "../numbers.helpers";

export type TUnmatchedBookingsLabelKey =
  "rideProduction.skippedPassengers" | "rideProduction.noMatchingRidesToJoin";

export interface IUnmatchedBookingsDisplayInput {
  totalBookingCount?: number;
  failedToSaveJoinedBookingCount?: number;
  joinedBookingCount?: number;
  producedBookingCount?: number;
  rideTooShortBookingCount?: number;
  notFoundRidesToJoinBookingCount?: number;
  skippedPassengerBookingCount?: number;
  allInputBookingIds?: number[];
  joinedBookingIds?: number[];
  producedBookingIds?: number[];
  rideTooShortBookingIds?: number[];
  notFoundRidesToJoinBookingIds?: number[];
  failedToSaveJoinedBookingIds?: number[];
  skippedPassengerBookingIds?: number[];
}

export interface IUnmatchedBookingsDisplay {
  hasSkippedPassengersMetric: boolean;
  unmatchedBookingsDisplayCount: number;
  unmatchedBookingsDisplayIds: number[];
  unmatchedBookingsLabelKey: TUnmatchedBookingsLabelKey;
}

/** Resolve unmatched-bookings count/ids/label for produce stats (preview + rich logs). */
export const resolveUnmatchedBookingsDisplay = ({
  totalBookingCount,
  failedToSaveJoinedBookingCount,
  joinedBookingCount,
  producedBookingCount,
  rideTooShortBookingCount,
  notFoundRidesToJoinBookingCount,
  skippedPassengerBookingCount,
  allInputBookingIds,
  joinedBookingIds,
  producedBookingIds,
  rideTooShortBookingIds,
  notFoundRidesToJoinBookingIds,
  failedToSaveJoinedBookingIds,
  skippedPassengerBookingIds,
}: IUnmatchedBookingsDisplayInput): IUnmatchedBookingsDisplay => {
  const skippedBookingCount =
    safeNumber(totalBookingCount) +
    safeNumber(failedToSaveJoinedBookingCount) -
    safeNumber(joinedBookingCount) -
    safeNumber(producedBookingCount);
  // Overlapping joined/produced counts must not yield a negative skipped remainder.
  const otherSkippedCount = Math.max(
    0,
    skippedBookingCount -
      safeNumber(rideTooShortBookingCount) -
      safeNumber(notFoundRidesToJoinBookingCount),
  );

  const otherSkippedBookingIds = (() => {
    if (!allInputBookingIds?.length || otherSkippedCount <= 0) {
      return [];
    }
    const known = new Set([
      ...(joinedBookingIds || []),
      ...(producedBookingIds || []),
      ...(rideTooShortBookingIds || []),
      ...(notFoundRidesToJoinBookingIds || []),
      ...(failedToSaveJoinedBookingIds || []),
    ]);
    return allInputBookingIds.filter(id => !known.has(id));
  })();

  const notFoundRidesToJoinCombinedCount =
    safeNumber(notFoundRidesToJoinBookingCount) + otherSkippedCount;
  const notFoundRidesToJoinCombinedIds = [
    ...(notFoundRidesToJoinBookingIds || []),
    ...otherSkippedBookingIds,
  ];

  const hasSkippedPassengersMetric =
    safeNumber(skippedPassengerBookingCount) > 0;
  const skippedPassengersCombinedCount = hasSkippedPassengersMetric
    ? safeNumber(skippedPassengerBookingCount) + otherSkippedCount
    : 0;
  const skippedPassengersCombinedIds = hasSkippedPassengersMetric
    ? [...(skippedPassengerBookingIds || []), ...otherSkippedBookingIds]
    : [];

  return {
    hasSkippedPassengersMetric,
    unmatchedBookingsDisplayCount: hasSkippedPassengersMetric
      ? skippedPassengersCombinedCount
      : notFoundRidesToJoinCombinedCount,
    unmatchedBookingsDisplayIds: hasSkippedPassengersMetric
      ? skippedPassengersCombinedIds
      : notFoundRidesToJoinCombinedIds,
    unmatchedBookingsLabelKey: hasSkippedPassengersMetric
      ? "rideProduction.skippedPassengers"
      : "rideProduction.noMatchingRidesToJoin",
  };
};
